import { useEffect, useLayoutEffect } from "react";
import {
  Box,
  Text,
  Spinner,
  Center,
  Flex,
  useBreakpoint,
  useToast,
} from "@chakra-ui/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { t } from "i18next";
import { apiClient } from "../../services/axiosInstance";
import { useAccountData } from "../../hooks/useAccountData";
import { useNavigate, useOutletContext } from "react-router";
import type { ShelfOutletContext } from "../shelf/MyShelfScreen";
import { setFunnelModalConfig } from "../../components/funnel/FunnelModal";
import { FUNNEL_TYPES } from "../../components/funnel/utils/contants";
import { EventContextArea } from "../../components/funnel/types";
import { useGetNavigationContext } from "../../hooks/useGetNavigationContext";
import { useIsSmallBreakpoint, useMobileBreakpoint } from "../../utils/useBreakpoints";
import type { GoldMonthlyResponse, GoldMonthlyTitle } from "./types";
import { useConfetti } from "./useConfetti";
import { GoldUpsellBanner } from "./GoldUpsellBanner";
import { MonthlyRewardCard } from "./MonthlyRewardCard";
import { WavyUnderlineIcon } from "../../components/icons/WavyUnderlineIcon";

const MONTH_NAME_FORMATTER = new Intl.DateTimeFormat("en-US", { month: "long" });

/** Resolve a 1-12 month number to its English name (e.g. 4 → "April"). */
function formatMonthName(month: number): string {
  // Day=1 keeps the formatter from leaking calendar quirks; year is irrelevant.
  return MONTH_NAME_FORMATTER.format(new Date(2000, month - 1, 1));
}

export function MonthlyComicsScreen() {
  const createToast = useToast();
  const queryClient = useQueryClient();
  const accountData = useAccountData();
  const navigate = useNavigate();

  // Clear the shelf shell's search bar — this tab has no search.
  const { setSearchConfig } = useOutletContext<ShelfOutletContext>();
  useLayoutEffect(() => {
    setSearchConfig(null);
  });

  // Gate on monthly-rewards feature flag — redirect home if disabled
  useEffect(() => {
    if (accountData && !accountData.features?.["monthly_rewards"]) {
      navigate("/", { replace: true });
    }
  }, [accountData, navigate]);
  const { canvasRef, fire: fireConfetti } = useConfetti();

  const isSmallBreakpoint = useIsSmallBreakpoint();
  const isMobile = useMobileBreakpoint();
  const breakpoint = useBreakpoint({ ssr: false });
  const isWideDesktop = ["xl", "2xl", "3xl", "4xl"].includes(breakpoint);

  const navigationContext = useGetNavigationContext({
    contextArea: EventContextArea.goldPage,
  });

  const isGold = !!accountData?.user?.gold;

  const openSubscriptionPaywall = () => {
    setFunnelModalConfig({
      type: FUNNEL_TYPES.subscription,
      context: {
        navigationContext,
      },
      options: {
        locked: false,
        onSuccess: () => {
          setFunnelModalConfig({
            type: FUNNEL_TYPES.congrats,
            context: {
              navigationContext,
            },
          });
        },
      },
    });
  };

  const { data: rawData, isLoading } = useQuery({
    queryKey: ["gold-monthly-public"],
    queryFn: async () => {
      const { data } = await apiClient.get("/v1/gold-monthly");
      return data?.payload?.results as GoldMonthlyResponse;
    },
  });

  const { mutate: claimTitle, isPending: isClaimPending, variables: claimingId } = useMutation({
    mutationFn: async (titleId: number) => {
      const { data } = await apiClient.post("/v1/gold-monthly/claim", {
        payload: { title_id: titleId },
        meta: {},
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["gold-monthly-public"] });
      fireConfetti();
      const status = data.payload?.results?.status;
      createToast({
        status: status === "already_owned" ? "info" : "success",
        description: status === "already_owned"
          ? t("screens.monthlyComics.toastAlreadyOwned")
          : t("screens.monthlyComics.toastClaimSuccess"),
        duration: 4000,
        isClosable: true,
      });
    },
    onError: (error) => {
      const errorCode = axios.isAxiosError<{ error?: string }>(error)
        ? error.response?.data?.error
        : undefined;
      const isAlreadyClaimed = errorCode === "ALREADY_CLAIMED";
      createToast({
        status: isAlreadyClaimed ? "info" : "error",
        description: isAlreadyClaimed
          ? t("screens.monthlyComics.toastAlreadyClaimed")
          : t("screens.monthlyComics.claimFailed"),
        duration: 5000,
        isClosable: true,
      });
      if (isAlreadyClaimed) {
        queryClient.invalidateQueries({ queryKey: ["gold-monthly-public"] });
      }
    },
  });

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100%" width="100%">
        <Spinner color="blaze.blaze" size="lg" />
      </Flex>
    );
  }

  const program = rawData?.reward;
  const titles: GoldMonthlyTitle[] = rawData?.titles ?? [];

  const headerMonth = formatMonthName(
    program?.month ?? new Date().getMonth() + 1,
  );

  return (
    <Flex
      overflow="auto"
      height="100%"
      width="100%"
      position="relative"
      flexDirection="column"
      alignItems="flex-start"
      pb="50px"
    >
      {/* Confetti canvas */}
      <Box
        as="canvas"
        ref={canvasRef}
        position="fixed"
        top={0}
        left={0}
        w="100%"
        h="100%"
        pointerEvents="none"
        zIndex={1000}
      />

      {/* Content wrapper */}
      <Flex
        flexDirection="column"
        position="relative"
        width="100%"
        maxWidth="1200px"
      >
        {/* ── Header section ── */}
        <Box
          as="section"
          position="relative"
          px={isSmallBreakpoint ? "20px" : "48px"}
          pt={isSmallBreakpoint ? "32px" : "48px"}
          pb={isSmallBreakpoint ? "16px" : "24px"}
        >
          <Flex direction="column" gap="16px" maxW="740px">
            {/* Title */}
            <Text
              fontFamily="Bricolage Grotesque"
              fontSize={isSmallBreakpoint ? "28px" : "40px"}
              fontWeight={700}
              color="white"
              lineHeight={1.2}
            >
              {program?.title ||
                t("screens.monthlyComics.headerTitle", { month: headerMonth })}
            </Text>

            {/* Subtitle */}
            <Text fontSize="16px" color="neutral.300" lineHeight={1.6}>
              {program?.description ||
                t("screens.monthlyComics.headerSubtitleFallback")}
            </Text>
          </Flex>
        </Box>

        {/* ── Page content ── */}
        <Flex
          flexDirection="column"
          px={isSmallBreakpoint ? "16px" : "48px"}
          pt="24px"
          gap="48px"
        >
          {/* Gold upsell banner (non-Gold only) */}
          {!isGold && (
            <GoldUpsellBanner
              onSubscribe={openSubscriptionPaywall}
              isSmall={isSmallBreakpoint}
            />
          )}

          {/* Comics grid */}
          {titles.length > 0 ? (
            <Flex flexDirection="column" gap="24px">
              {/* Section header */}
              <Flex direction="column" gap="16px">
                <Box height="1px" width="100%" bg="whiteAlpha.200" />
                <Flex direction="column" gap="4px">
                  <Text
                    fontSize="24px"
                    fontWeight={700}
                    color="dune.dune"
                    fontFamily="Bricolage Grotesque"
                  >
                    {t("screens.monthlyComics.thisMonthsSelection")}
                  </Text>
                  <WavyUnderlineIcon color="blaze.blaze" w="60px" h="8px" />
                </Flex>
              </Flex>

              {/* Horizontal list when < 3 titles, 4-column grid otherwise */}
              {titles.length < 3 && !isSmallBreakpoint ? (
                <Flex direction="column" gap="20px">
                  {titles.map((title) => (
                    <MonthlyRewardCard
                      key={title.id}
                      titleData={title}
                      layout="horizontal"
                      isClaiming={isClaimPending && claimingId === title.id}
                      isGold={isGold}
                      onClaim={() => claimTitle(title.id)}
                      onSubscribe={openSubscriptionPaywall}
                    />
                  ))}
                </Flex>
              ) : (
                <Flex
                  flexWrap="wrap"
                  gap={isMobile ? "16px" : isWideDesktop ? "40px" : "24px"}
                  sx={{
                    "& > *": {
                      /* mobile: 2 cols, md–lg: 3 cols, xl+: 4 cols */
                      flexBasis: isMobile
                        ? "calc(50% - 8px)"
                        : isWideDesktop
                          ? "calc(25% - 30px)"
                          : "calc(33.333% - 16px)",
                      maxWidth: isMobile
                        ? "calc(50% - 8px)"
                        : isWideDesktop
                          ? "calc(25% - 30px)"
                          : "calc(33.333% - 16px)",
                    },
                  }}
                >
                  {titles.map((title) => (
                    <MonthlyRewardCard
                      key={title.id}
                      titleData={title}
                      isClaiming={isClaimPending && claimingId === title.id}
                      isGold={isGold}
                      onClaim={() => claimTitle(title.id)}
                      onSubscribe={openSubscriptionPaywall}
                    />
                  ))}
                </Flex>
              )}
            </Flex>
          ) : (
            <Center py={16}>
              <Text color="whiteAlpha.500" fontSize="16px">
                {t("screens.monthlyComics.noTitlesAvailable")}
              </Text>
            </Center>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
